import React, { useMemo } from "react";
import { Box, Grid, Paper, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { LineChart, PieChart } from "@mui/x-charts-pro";
import { useTranslation } from "react-i18next";
import { format, getISOWeek } from "date-fns";
import { nl, enGB, fr, de } from "date-fns/locale";

// Blue palette: primary → progressively lighter
const CHART_PALETTE = [
  "#1976D2", "#1E88E5", "#2196F3", "#42A5F5", "#64B5F6",
  "#90CAF9", "#BBDEFB", "#E3F2FD", "#1565C0", "#0D47A1",
];


const getDateLocale = (language) => {
  switch (language) {
    case "en": return enGB;
    case "fr": return fr;
    case "de": return de;
    default: return nl;
  }
};

const DashboardCharts = ({ data, loading, duration }) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isPieStacked = useMediaQuery(theme.breakpoints.down("lg"));
  const chartHeight = isMobile ? 280 : 350;
  const pieChartHeight = 350;
  const locale = useMemo(() => getDateLocale(i18n.language), [i18n.language]);

  // When pie charts are full-width (stacked): legend below; when side-by-side: legend on the right
  const pieLegendProps = isPieStacked
    ? {
        slotProps: {
          legend: {
            direction: "row",
            position: { vertical: "bottom", horizontal: "middle" },
          },
        },
      }
    : {};

  // Map dashboard duration to chart granularity (same as backend)
  const chartDuration = useMemo(() => {
    switch (duration) {
      case "yearly": return "monthly";
      case "quarterly": return "weekly";
      default: return "daily";
    }
  }, [duration]);

  const formatPeriodLabel = (periodStart) => {
    if (!periodStart) return "";
    const date = new Date(periodStart + "T00:00:00");
    switch (chartDuration) {
      case "daily": return format(date, "dd MMM", { locale });
      case "weekly": return `W${getISOWeek(date)} ${date.getFullYear()}`;
      case "monthly": return format(date, "MMM yyyy", { locale });
      default: return format(date, "MMM yyyy", { locale });
    }
  };

  const timeSeries = data?.timeSeries || [];
  const productServiceBreakdown = data?.productServiceBreakdown || [];
  const subcontractorBreakdown = data?.subcontractorBreakdown || [];
  const completedCount = data?.completedCount || 0;
  const rejectedCount = data?.rejectedCount || 0;

  const arcLabel = (item, total) => {
    const pct = Math.round((item.value / total) * 100);
    return pct >= 5 ? `${pct}%` : "";
  };

  const renderNoData = () => (
    <Box className={classes.noData}>
      <Typography color="text.secondary">
        {t("dashboard.chart.noData")}
      </Typography>
    </Box>
  );

  return (
    <Grid container spacing={2}>
      {/* Row 1: Line Chart | Donut Chart */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper className={classes.chartCard} elevation={1}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t("dashboard.chart.trend")}
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={chartHeight} sx={{ borderRadius: 1 }} />
          ) : timeSeries.length > 0 ? (
            <LineChart
              height={chartHeight}
              xAxis={[
                {
                  data: timeSeries.map((_, i) => i),
                  valueFormatter: (v) => formatPeriodLabel(timeSeries[v]?.periodStart),
                  scaleType: "point",
                },
              ]}
              series={[
                {
                  data: timeSeries.map((d) => d.newCount),
                  label: t("dashboard.chart.newSeries"),
                  color: "#1976D2",
                },
                {
                  data: timeSeries.map((d) => d.resolvedCount),
                  label: t("dashboard.chart.resolvedSeries"),
                  color: "#78909C",
                },
              ]}
            />
          ) : (
            renderNoData()
          )}
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Paper className={classes.chartCard} elevation={1}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t("dashboard.chart.resolution")}
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={chartHeight} sx={{ borderRadius: 1 }} />
          ) : completedCount + rejectedCount > 0 ? (
            <PieChart
              height={pieChartHeight}
              series={[
                {
                  data: [
                    {
                      id: 0,
                      value: completedCount,
                      label: t("dashboard.chart.completed"),
                      color: "#1976D2",
                    },
                    {
                      id: 1,
                      value: rejectedCount,
                      label: t("dashboard.chart.rejected"),
                      color: "#90CAF9",
                    },
                  ],
                  innerRadius: 50,
                  arcLabel: (item) => arcLabel(item, completedCount + rejectedCount),
                },
              ]}
              {...pieLegendProps}
              sx={{ "& .MuiPieArc-arcLabel": { fill: "#fff", fontWeight: 600, fontSize: 14 } }}
            />
          ) : (
            renderNoData()
          )}
        </Paper>
      </Grid>

      {/* Row 2: Product/Service | Subcontractor */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper className={classes.chartCard} elevation={1}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t("dashboard.chart.productService")}
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={chartHeight} sx={{ borderRadius: 1 }} />
          ) : productServiceBreakdown.length > 0 ? (
            <PieChart
              height={pieChartHeight}
              series={[
                {
                  data: productServiceBreakdown.map((item, i) => ({
                    id: i,
                    value: item.count,
                    label: item.name,
                    color: CHART_PALETTE[i % CHART_PALETTE.length],
                  })),
                  arcLabel: (item) => arcLabel(item, productServiceBreakdown.reduce((s, x) => s + x.count, 0)),
                },
              ]}
              {...pieLegendProps}
              sx={{ "& .MuiPieArc-arcLabel": { fill: "#fff", fontWeight: 600, fontSize: 14 } }}
            />
          ) : (
            renderNoData()
          )}
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Paper className={classes.chartCard} elevation={1}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t("dashboard.chart.subcontractor")}
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={chartHeight} sx={{ borderRadius: 1 }} />
          ) : subcontractorBreakdown.length > 0 ? (
            <PieChart
              height={pieChartHeight}
              series={[
                {
                  data: subcontractorBreakdown.map((item, i) => ({
                    id: i,
                    value: item.count,
                    label: item.name,
                    color: CHART_PALETTE[i % CHART_PALETTE.length],
                  })),
                  arcLabel: (item) => arcLabel(item, subcontractorBreakdown.reduce((s, x) => s + x.count, 0)),
                },
              ]}
              {...pieLegendProps}
              sx={{ "& .MuiPieArc-arcLabel": { fill: "#fff", fontWeight: 600, fontSize: 14 } }}
            />
          ) : (
            renderNoData()
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles((theme) => ({
  chartCard: {
    padding: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius * 2,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    breakInside: "avoid",
  },
  noData: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexGrow: 1,
    minHeight: 200,
  },
}));

export default DashboardCharts;
